import { PlayerEvents, Flags, AdobePass } from 'enums';
import { normalizeQueryString } from 'lib/format';
import { isEmpty } from 'lib/util';
import {
    fetchEventOrVideoByQuery,
    createRequestPageAction,
    createReceivePageAction,
    completePageLoad
} from 'common/page/actions';
import { updateAdobeAuthentication } from 'webapp/syndicated/actions';
import { getEventByQuery } from 'common/actions/listingsActions';
import { updateRoute, contentUnSelect, streamUnSelect, contentSelect } from 'common/actions/appActions';
import { createAutoplayUpdatedAction } from 'webapp/player/actions';
import { AdobePassProxyLoginComponent } from 'common/page/AdobePassProxyLogin';
import { domainAuthStatus } from './SyndicatedPlayer';
import WatchESPNWebPlayer from 'webapp/common/video-player';

const PARTNER = 'watchespn';
const DOMAIN = 'syndicated-api';

const { Env: AdobePassEnv } = AdobePass;

class SyndicatedPlayerAPIProxy {
    constructor(component) {
        const {
            props
        } = component;
        const {
            adobePassLogout,
            location: { query: { referrer: referrerQuery } = {} }
        } = props;

        this.targetWindow = window.parent;
        const { referrer = '' } = document;
        // `referrerQuery` is for when it's self-reload and lose the original `document.referrer`.
        const originResult = /https?:\/\/[^\/]+/.exec(referrerQuery || referrer);
        // try to specify the target origin, but you won't be able to know the parent location in old IE.
        this.targetOrigin = (originResult && originResult[0]) || '*';
        this.component = component;
        const { props: { location: { query: { instanceId = '' } } } } = this.component;
        this.instanceId = instanceId;
        this.adobePassLogout = adobePassLogout.bind(this);
        this._onPostMessage = this._onPostMessage.bind(this);
        this._onPlaying = this._onPlaying.bind(this);
        this._onPlay = this._onPlay.bind(this);
        this._onPause = this._onPause.bind(this);
        this._onAdPodStarted = this._onAdPodStarted.bind(this);
        this._onAdPodEnded = this._onAdPodEnded.bind(this);
        this._onAdStarted = this._onAdStarted.bind(this);
        this._onAdEnded = this._onAdEnded.bind(this);
        this._onProgramChange = this._onProgramChange.bind(this);
        this._onContentLoaded = this._onContentLoaded.bind(this);
        this._onContentEnded = this._onContentEnded.bind(this);
        this._onError = this._onError.bind(this);
        this._unsubscribe = this._subscribeMessage();
        this._publish(SyndicatedPlayerAPIProxy.Events.BRIDGE_READY);
    }

    componentWillReceiveProps(nextProps) {
        const {
            domainAuth: nextDomainAuth,
            providerInfo: nextProviderInfo,
            providerDialog: { display: nextDisplay },
            player: { embedded: nextEmbedded },
            query: nextQuery,
            content: nextContent
        } = nextProps;
        const { query, domainAuth, providerInfo, providerDialog: { display }, player: { embedded } } = this.component.props;
        if (domainAuth === domainAuthStatus.UNKNOWN && nextDomainAuth !== domainAuth) {
            this._publish(SyndicatedPlayerAPIProxy.Events.READY, { blocked: nextDomainAuth === domainAuthStatus.NOT_ALLOWED });
        }
        if (isEmpty(providerInfo) && !isEmpty(nextProviderInfo)) {
            this._publish(SyndicatedPlayerAPIProxy.Events.PROVIDER_SELECTED, { provider: this.getSelectedProvider(nextProps) });
        } else if (!isEmpty(providerInfo) && isEmpty(nextProviderInfo)) {
            this._publish(SyndicatedPlayerAPIProxy.Events.PROVIDER_LOGOUT);
        }
        if (nextDisplay && !display) {
            this._publish(SyndicatedPlayerAPIProxy.Events.PROVIDER_SCREEN_SHOWN);
        }
        if (nextEmbedded && !embedded && !this._playerUnsubscribe) {
            this._addPlayerListeners();
        } else if (!nextEmbedded && embedded && this._playerUnsubscribe) {
            this._removePlayerListeners();
        }
        // This should only be triggered when the `id` URL query is updated by schedule link click
        // When content is switched through API, content changes first followed by URL query update,
        // so it doesn't hit this condition
        if (!this._selecting && nextQuery.id && nextQuery.id !== query.id) {
            this.selectEventById(nextQuery.id);
            this._selecting = true;
        } else if (this._selecting && nextQuery.id && nextContent.id &&
            nextQuery.id === `${nextContent.id}` && nextQuery.id === query.id) {
            this._selecting = false;
        }
    }

    parentHost(host) {
        // this is called from the parent window in response to `BRIDGE_READY` event from the player,
        // when this is called, we assume the parent is handling the postMessage the player is sending
        // so it can also handle reloading the iframe when the mvpd login completes.
        this.hasBridgedParent = true;
        this.component.parentHost(host);
    }

    _addPlayerListeners() {
        if (!this.videoPlayer) {
            return;
        }
        let player = this.videoPlayer.player;
        if (player) {
            player.on(PlayerEvents.ESPN_PLAYING, this._onPlaying);
            player.on(PlayerEvents.ESPN_PLAY, this._onPlay);
            player.on(PlayerEvents.ESPN_PAUSE, this._onPause);
            player.on(PlayerEvents.ESPN_AD_POD_STARTED, this._onAdPodStarted);
            player.on(PlayerEvents.ESPN_AD_POD_ENDED, this._onAdPodEnded);
            player.on(PlayerEvents.ESPN_AD_STARTED, this._onAdStarted);
            player.on(PlayerEvents.ESPN_AD_ENDED, this._onAdEnded);
            player.on(PlayerEvents.ESPN_PROGRAM_CHANGE, this._onProgramChange);
            player.on(PlayerEvents.ESPN_CONTENT_LOADED, this._onContentLoaded);
            player.on(PlayerEvents.ESPN_CONTENT_ENDED, this._onContentEnded);
            player.on(PlayerEvents.ESPN_ERROR, this._onError);
        }
        this._playerUnsubscribe = () => {
            if (!this.videoPlayer) {
                return;
            }
            player = this.videoPlayer.player;
            if (player) {
                player.off(PlayerEvents.ESPN_PLAYING, this._onPlaying);
                player.off(PlayerEvents.ESPN_PLAY, this._onPlay);
                player.off(PlayerEvents.ESPN_PAUSE, this._onPause);
                player.off(PlayerEvents.ESPN_AD_POD_STARTED, this._onAdPodStarted);
                player.off(PlayerEvents.ESPN_AD_POD_ENDED, this._onAdPodEnded);
                player.off(PlayerEvents.ESPN_AD_STARTED, this._onAdStarted);
                player.off(PlayerEvents.ESPN_AD_ENDED, this._onAdEnded);
                player.off(PlayerEvents.ESPN_PROGRAM_CHANGE, this._onProgramChange);
                player.off(PlayerEvents.ESPN_CONTENT_LOADED, this._onContentLoaded);
                player.off(PlayerEvents.ESPN_CONTENT_ENDED, this._onContentEnded);
                player.off(PlayerEvents.ESPN_ERROR, this._onError);
            }
        };
    }

    _removePlayerListeners() {
        if (this._playerUnsubscribe) {
            this._playerUnsubscribe();
            this._playerUnsubscribe = null;
        }
    }

    async _updateHeroByQuery(queryArg, autoplayArg, thumbnail) {
        const query = normalizeQueryString(queryArg);
        const {
            dispatch,
            page: { id: pageId, name: pageName },
            player: { embedded },
            query: { autoStart },
            content
        } = this.component.props;
        dispatch(createRequestPageAction(pageId));
        dispatch(completePageLoad());
        const payload = await dispatch(fetchEventOrVideoByQuery(query));
        dispatch(contentUnSelect());
        dispatch(streamUnSelect());
        const { page: { edition, contents: heroContent } = {} } = payload || {};
        if (thumbnail) {
            heroContent.imageHref = thumbnail;
        }

        if (heroContent && content && (heroContent.id !== content.id)) {
            dispatch(contentSelect(heroContent));
        }

        if (embedded && autoplayArg === false) {
            const connectedPlayer = this.connectedPlayer;
            if (connectedPlayer) {
                await connectedPlayer.unembedPlayer();
            }
        }
        dispatch(createReceivePageAction({
            id: pageId,
            name: pageName,
            edition,
            heroContent,
            isFinal: true
        }));

        let autoplay = autoplayArg;
        if (autoplay == null) {
            if (embedded) {
                autoplay = true;
            } else if (autoStart === 'false') {
                autoplay = false;
            }
        }
        const hasAutoplay = autoplay != null;
        if (hasAutoplay) {
            // TODO: clarify the difference between `autoEmbed` and `autoplay`
            dispatch(createAutoplayUpdatedAction(autoplay));
        }
        dispatch(updateRoute(hasAutoplay ? { ...query, autoStart: autoplay } : query));
    }

    get connectedPlayer() {
        const { connectedPlayer: connectedPlayerWrapper } = this.component;
        let player;
        if (connectedPlayerWrapper) {
            // we assume `ConnectedPlayer` always use `connectPlayer` HoC which uses redux `connect`.
            const connectedPlayer = connectedPlayerWrapper.getWrappedInstance();
            if (connectedPlayer) {
                // get the instance from injectIntl HoC or maybe not using HoC any more
                player = connectedPlayer.getWrappedInstance() || connectedPlayer;
            }
        }
        return player;
    }

    get videoPlayer() {
        let playerBundleInstance;
        if (this.connectedPlayer) {
            playerBundleInstance = this.connectedPlayer.videoPlayer &&
                this.connectedPlayer.videoPlayer.getPlayer(WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE);
        }
        return playerBundleInstance;
    }

    playVideo() {
        if (!this.videoPlayer) {
            return;
        }
        this.videoPlayer.play();
    }

    pauseVideo() {
        if (!this.videoPlayer) {
            return;
        }
        this.videoPlayer.pause();
    }

    muteStream() {
        if (!this.videoPlayer || !this.videoPlayer.mute) {
            return;
        }
        this.videoPlayer.mute();
    }

    unMuteStream() {
        if (!this.videoPlayer || !this.videoPlayer.unmute) {
            return;
        }
        this.videoPlayer.unmute();
    }

    selectById(id, autoplay, thumbnail = '') {
        if (!id) {
            return;
        }
        this._updateHeroByQuery({ id }, autoplay, thumbnail);
    }

    selectByEventId(eventId, autoplay, thumbnail = '') {
        if (!eventId) {
            return;
        }
        this._updateHeroByQuery({ eventId }, autoplay, thumbnail);
    }

    selectEventByNetworkName(network, thumbnail = '') {
        if (!network) {
            return;
        }
        this._updateHeroByQuery({ network }, undefined, thumbnail);
    }

    /*
    selectByAiringId(
    token,
    tokenType,
    sessionId,
    viewContentReference,
    airingId,
    pricePlanId,
    errorCode = null, errorMessage = null, degradation = null, autoplay = false, thumbnail = '') {
    // TODO: figure out if we need it or not
    }
    */

    selectEventById(id, thumbnail = '') {
        this.selectById(id, undefined, thumbnail);
    }

    selectEventByGameIdLeague(gameId, league = '', thumbnail = '') {
        const { dispatch } = this.component.props;
        dispatch(getEventByQuery({ eventId: gameId, league }))
            .then(content => {
                if (!content) {
                    return;
                }
                this.selectById(content.id, true, thumbnail);
            });
    }

    selectEventByExternalId(gameId, sport, leagueName) {
        this.selectEventByGameIdLeague(gameId, leagueName);
    }

    setVideoPosition(positionInSec) {
        if (!this.videoPlayer || !this.videoPlayer.currentTime) {
            return;
        }
        this.videoPlayer.currentTime(positionInSec);
    }

    getVideoPosition() {
        if (!this.videoPlayer || !this.videoPlayer.currentTime) {
            return 0;
        }
        return this.videoPlayer.currentTime() || 0;
    }

    /*
    getFeedById(id) {
        // TODO: figure out if we need it or not
    }
    */

    enterFullScreen() {
        if (!this.videoPlayer || !this.videoPlayer.requestFullscreen) {
            return;
        }
        this.videoPlayer.requestFullscreen();
    }

    exitFullScreen() {
        if (!this.videoPlayer || !this.videoPlayer.exitFullscreen) {
            return;
        }
        this.videoPlayer.exitFullscreen();
    }

    /*
    sendMetric(metric) {
      // TODO: figure out if we need it or not
    }
    */

    /*
    getMetadataById(key) {
      // TODO: figure out if we need it or not
    }
    */

    getAffiliate() {
        const { providerInfo = {} } = this.component.props;
        if (isEmpty(providerInfo)) {
            return null;
        }
        return {
            id: providerInfo.abbreviation,
            name: providerInfo.name,
            websiteUrl: providerInfo.links.web.href,
            imageUrl: providerInfo.images.logo.href
        };
    }

    getSelectedProvider(props = this.component.props) {
        // TODO: `espn3Only` prop is returned from http://broadband.espn.com/espn3/auth/watchespn/userData
        const { providerInfo = {} } = props;
        if (isEmpty(providerInfo)) {
            return null;
        }
        return {
            adobePassProviderId: providerInfo.abbreviation,
            displayName: providerInfo.name,
            providerHomepageUrl: providerInfo.links.web.href,
            providerLogoUrl: providerInfo.images.logo.href,
            name: providerInfo.abbreviation
        };
    }

    getAuthenticationStatus() {
        const { authStatus, providerInfo = {} } = this.component.props;
        if (authStatus === 'not authenticated') {
            return {
                name: 'invalid',
                type: 'none'
            };
        }

        return {
            name: providerInfo.name,
            type: authStatus === 'authenticated' ? 'tve' : 'ip'
        };
    }

    recheckAccess() {
        if (this.targetWindow && this.targetWindow !== window) {
            this._publish(SyndicatedPlayerAPIProxy.Events.RECHECK_ACCESS, { href: window.location.href });
        } else {
            this.component.recheckAccess();
        }
    }

    /*
    getPlayingEvents() {
      // TODO: figure out if we need it or not
    }
    */

    _onPlaying() {
        this._publish(SyndicatedPlayerAPIProxy.Events.PLAYING);
    }

    _onPlay() {
        this._publish(SyndicatedPlayerAPIProxy.Events.PLAY);
    }

    _onPause() {
        this._publish(SyndicatedPlayerAPIProxy.Events.PAUSE);
    }

    _onAdPodStarted(_, payload) {
        this._publish(SyndicatedPlayerAPIProxy.Events.AD_POD_STARTED, payload);
    }

    _onAdPodEnded(_, payload) {
        this._publish(SyndicatedPlayerAPIProxy.Events.AD_POD_ENDED, payload);
    }

    _onAdStarted(_, payload) {
        this._publish(SyndicatedPlayerAPIProxy.Events.AD_STARTED, payload);
    }

    _onAdEnded(_, payload) {
        this._publish(SyndicatedPlayerAPIProxy.Events.AD_ENDED, payload);
    }

    _onProgramChange(_, mediainfo) {
        // guard against program change called with the same id
        // `playingStream` is passed by `playable` HOC
        const { playingStream = {} } = this.component.props;
        if (playingStream.id === mediainfo.id) {
            return;
        }
        this._publish(SyndicatedPlayerAPIProxy.Events.PROGRAM_CHANGE, mediainfo);
    }

    _onContentLoaded(_, { mediainfo = {} } = {}) {
        this._publish(SyndicatedPlayerAPIProxy.Events.CONTENT_LOADED, mediainfo);
    }

    _onContentEnded() {
        this._publish(SyndicatedPlayerAPIProxy.Events.CONTENT_ENDED);
    }

    _onError(_, payload) {
        this._publish(SyndicatedPlayerAPIProxy.Events.ERROR, payload);
    }

    async _onPostMessage(event) {
        // Supposed to check `event.origin` here but then we need to maintain whitelist...

        // One thing we can't pass with postMessage is a function whether we send a message as a string or an object,
        // hopefully we'll never have to allow the integration to pass a function.
        if (typeof event.data !== 'string') {
            return;
        }

        let data;
        try {
            data = JSON.parse(event.data);
        } catch (err) {
            data = {};
        }

        const { type, tId, status, params, entitlementToken } = data;

        if (!type) {
            return;
        }

        const [partner, domain, apiName] = type.split(':');

        if (partner !== 'watchespn') {
            return;
        }

        if (domain === 'syndicated-api') {
            if (this[apiName]) {
                const args = Array.isArray(data.payload) ? data.payload : [data.payload];
                const payload = this[apiName](...args);
                if (SyndicatedPlayerAPIProxy.Getter.indexOf(apiName) !== -1) {
                    if (payload && typeof payload.then === 'function') {
                        payload.then(value => {
                            this._publish(type, value, tId);
                        });
                    } else {
                        this._publish(type, payload, tId);
                    }
                }
            }
        } else if (
            domain === 'adobepass-login' &&
            type === AdobePassProxyLoginComponent.EVENT_LOADED
        ) {
            const val = window.localStorage.getItem(AdobePassProxyLoginComponent.XD_LOCALSTORAGE_KEY);
            if (val !== AdobePassProxyLoginComponent.XD_LOCALSTORAGE_VAL) {
                // enable this to auto-kick off self window login flow when xdomain storage failure is detected
                /*
                if (!this.component.loginWindow) {
                    return;
                }
                const origin = `${window.location.protocol}//${window.location.host}`;
                this.component.loginWindow.postMessage(JSON.stringify({
                    type: AdobePassProxyLoginComponent.EVENT_CLOSE_REQUEST,
                    params: -1
                }), origin);
                */
                this._xdomainBlocked = true;
            }
        } else if (
            domain === 'adobepass-login' &&
            type === AdobePassProxyLoginComponent.EVENT_CLOSED
        ) {
            this.component.onLoginClose();
            // when the login screen is closed as a result of the request made due to xdomain storage failure is detected,
            // kick off the login flow using the self window
            if (params === -1) {
                this.component.onXDomainCheckFail();
            }
        } else if (
            domain === 'adobepass-login' &&
            type === AdobePassProxyLoginComponent.EVENT_AUTHENTICATE &&
            status === 'success'
        ) {
            const { dispatch, adobePassEnv = AdobePassEnv.PROD } = this.component.props;

            this.authSuccess = true;

            if (entitlementToken && this._xdomainBlocked) {
                try {
                    // update `entitlementToken` in the player window
                    window.localStorage.setItem('entitlementToken', entitlementToken);
                    // update `entitlementToken` in the AccessEnabler Proxy window, which is the source of truth
                    // FIXME: this is a hack to get around cross domain localStorage restriction in Safari
                    if (window.accessEnablerProxy && window.accessEnablerProxy.contentWindow) {
                        window.accessEnablerProxy.contentWindow.postMessage({
                            id: 'c.a.p.pm.s.espn',
                            operation: 'setItem',
                            params: ['entitlementToken', entitlementToken]
                        }, adobePassEnv === AdobePassEnv.STAGING ? 'https://sp.auth-staging.adobe.com' : 'https://sp.auth.adobe.com');
                    }
                } catch (err) {
                    console.error(err);
                }
            }

            // This needs to be executed before publishing `AUTHENTICATION_SUCCESS` event
            // Xfinity/Comcast will redirect the page to their login complete page when this(full authentication) is executed
            const isAuthenticated = await dispatch(updateAdobeAuthentication());

            if (this.targetWindow && this.targetWindow !== window) {
                // TODO: remove reload logic from `watchespn-syndicated-player` module
                // so that `authRetry` message logic can be executed
                this._publish(SyndicatedPlayerAPIProxy.Events.AUTHENTICATION_SUCCESS, {
                    href: window.location.href,
                    provider: this.getSelectedProvider(),
                    authenticationStatus: this.getAuthenticationStatus()
                });
            }

            this.component.onLoginClose();

            if (isAuthenticated) {
                this.component.onAuthSuccess();
            }

            if (this._xdomainBlocked) {
                // This can happen when `Cookie and website data` setting is set anything other than `Always Allow` in Safari
                this.reload(true, this._xdomainBlocked);
            }
        }
    }

    reload(authFailed = false, xdomainBlocked = false) { // eslint-disable-line class-methods-use-this
        // not recommended to use as this can cause other services which uses `postMessage`
        // to error out when they don't check the referrer origin change
        // currently reload button triggers this as a fallback when the automatic playback doesn't happen after login
        let qs = '';
        if (window.location.search.indexOf('referrer') === -1) {
            qs = `${window.location.search.indexOf('?') === -1 ? '?' : '&'}referrer=${encodeURIComponent(document.referrer)}`;
        }
        if (authFailed && window.location.search.indexOf(Flags.AUTH_RETRY) === -1) {
            qs += `&${Flags.AUTH_RETRY}=true`;
        }
        if (xdomainBlocked && window.location.search.indexOf(Flags.XDOMAIN_BLOCKED) === -1) {
            qs += `&${Flags.XDOMAIN_BLOCKED}=true`;
        }
        const reloadUrl = `${window.location.href}${qs}`;
        window.location.href = reloadUrl;
    }

    _publish(type, payload, tId) {
        if (!this.targetWindow || this.targetWindow === window) {
            return;
        }
        const event = {
            type,
            tId,
            payload
        };
        try {
            this.targetWindow.postMessage(
                `${PARTNER}:${DOMAIN}:${this.instanceId}${JSON.stringify(event)}`,
                this.targetOrigin
            );
        } catch (err) {
            console.error(err);
        }
    }

    _subscribeMessage() {
        window.addEventListener('message', this._onPostMessage);
        return function _unsubscribe() {
            window.removeEventListener('message', this._onPostMessage);
        };
    }

    destroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }
}

const API = {
    PLAY: 'playVideo',
    PAUSE: 'pauseVideo',
    MUTE: 'muteStream',
    UNMUTE: 'unMuteStream',
    SET_CURRENT_TIME: 'setVideoPosition',
    GET_CURRENT_TIME: 'getVideoPosition',
    ENTER_FULLSCREEN: 'enterFullScreen',
    EXIT_FULLSCREEN: 'exitFullScreen',
    SELECT_BY_ID: 'selectById',
    SELECT_BY_EVENT_ID: 'selectByEventId',
    SELECT_EVENT_BY_ID: 'selectEventById',
    SELECT_EVENT_BY_NETWORK_NAME: 'selectEventByNetworkName',
    SELECT_EVENT_BY_GAME_ID: 'selectEventByGameIdLeague',
    SELECT_EVENT_BY_EXTERNAL_ID: 'selectEventByExternalId',
    RECHECK_ACCESS: 'recheckAccess',
    ADOBE_PASS_LOGOUT: 'adobePassLogout',
    PARENT_HOST: 'parentHost',
    GET_AFFILIATE: 'getAffiliate',
    GET_SELECTED_PROVIDER: 'getSelectedProvider',
    GET_AUTH_STATUS: 'getAuthenticationStatus'
};

const Events = {
    BRIDGE_READY: 'bridgeReady',
    READY: 'ready',
    PROVIDER_SELECTED: 'providerSelected',
    PROVIDER_LOGOUT: 'providerLogout',
    PROVIDER_SCREEN_SHOWN: 'providerScreenShown',
    AUTHENTICATION_SUCCESS: 'authenticationSuccessful',
    RECHECK_ACCESS: 'recheckAccess',
    PLAYING: 'playing',
    PLAY: 'play',
    PAUSE: 'pause',
    AD_POD_STARTED: 'adPodStarted',
    AD_POD_ENDED: 'adPodEnded',
    AD_STARTED: 'adStarted',
    AD_ENDED: 'adEnded',
    PROGRAM_CHANGE: 'programChange',
    CONTENT_LOADED: 'contentLoaded',
    CONTENT_ENDED: 'contentEnded',
    ERROR: 'error'
};

SyndicatedPlayerAPIProxy.PARTNER = PARTNER;
SyndicatedPlayerAPIProxy.DOMAIN = DOMAIN;

Object.keys(API).forEach(key => {
    SyndicatedPlayerAPIProxy[key] = `${PARTNER}:${DOMAIN}:${API[key]}`;
});

SyndicatedPlayerAPIProxy.Events = {};
Object.keys(Events).forEach(key => {
    SyndicatedPlayerAPIProxy.Events[key] = `${PARTNER}:${DOMAIN}:${Events[key]}`;
});

SyndicatedPlayerAPIProxy.Getter = [
    API.GET_CURRENT_TIME,
    API.GET_AFFILIATE,
    API.GET_SELECTED_PROVIDER,
    API.GET_AUTH_STATUS
];

export default SyndicatedPlayerAPIProxy;



// WEBPACK FOOTER //
// ./app/webapp/syndicated/SyndicatedPlayerAPIProxy.js